import Icon from 'components/Icon';
import * as React from 'react';
import { routingEnabled } from 'utils/ui/app';
import { Link } from 'react-router';
import { classNames } from 'utils/ui/css'; // Custom classNames function

export interface IButtonProps {
  to?: string;
  onClick?: (e: React.MouseEvent) => void; // Should not be used for navigation if "to" is also used
  text?: string;
  icon?: string;
  iconAlign?: 'left' | 'right'; // Align icon to left or right
  svgClassName?: string; // For custom SVG styling (like stroke color)
  textClassName?: string; // For custom text styling (like font size)
  className?: string; // For custom button styling
  ariaLabel?: string; // For accessibility
  target?: string; // For external links
  disabled?: boolean;
  part?: string;
}

const Button: React.FC<IButtonProps> = ({
  to,
  onClick,
  text,
  icon,
  svgClassName = 'stroke-current text-gray-900',
  textClassName = 'text-sm',
  className = '',
  ariaLabel,
  iconAlign = 'left',
  disabled = false,
  ...rest
}) => {
  let Element = to ? Link : 'button';

  if (!routingEnabled()) {
    Element = to ? 'a' : 'button';
  }

  const onClickHandler = React.useCallback(
    (e: React.MouseEvent) => {
      if (onClick) {
        onClick(e);
      }
    },
    [onClick]
  );

  return (
    // @ts-expect-error ignore this error
    <Element
      {...(to ? (Element === 'a' ? { href: to } : { to }) : {})}
      onClick={onClickHandler}
      {...(Element === 'button' ? { disabled } : {})}
      aria-label={ariaLabel || text || 'Button'}
      role="button"
      className={classNames(
        className,
        'flex items-center space-x-1 p-2',
        'transition-opacity duration-300 hover:opacity-80',
        text ? 'justify-start' : 'justify-center', // Center if no text
        iconAlign?.toLowerCase() === 'right' ? 'flex-row-reverse' : 'flex-row' // Align icon to right
      )}
      {...rest}
    >
      {icon && <Icon part={icon} className={`inline-block ${icon} ${svgClassName}`} />}
      {text && (
        <span
          className={classNames(
            'ex3d-button-text',
            textClassName,
            'text-sm',
            icon && iconAlign?.toLowerCase() === 'left' ? 'pr-2' : 'px-2'
          )}
        >
          {text}
        </span>
      )}
    </Element>
  );
};

export default Button;
